import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      await fetch('https://readdy.ai/api/form/d6kem514a3uojp5ffrt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Get In Touch
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            We'd Love to<br />
            <span className="text-amber-900">Hear From You</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Have a question about our services or coaching programs? Reach out and we'll respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Trust Statement */}
              <div className="bg-amber-900 rounded-2xl p-8 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">We Respond Within 24 Hours</h3>
                <p className="text-amber-100 text-sm leading-relaxed">
                  Every message is read and responded to personally. We don't use bots or automated replies for initial inquiries.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-5">
                <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-amber-900"></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Email</p>
                    <a
                      href="mailto:info@upgradedversion.com"
                      className="text-gray-900 font-medium hover:text-amber-900 transition-colors text-sm"
                    >
                      info@upgradedversion.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-amber-900"></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Phone / WhatsApp</p>
                    <a
                      href="tel:+144378718881"
                      className="text-gray-900 font-medium hover:text-amber-900 transition-colors text-sm"
                    >
                      +1 443 787 18881
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-2xl">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-amber-900"></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Location</p>
                    <p className="text-gray-900 font-medium text-sm">1942 Broadway Street, Suite 314C</p>
                    <p className="text-gray-500 text-xs mt-0.5">Boulder, Colorado 80302, USA</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Follow Us</p>
                <div className="flex gap-3">
                  {[
                    { icon: 'ri-instagram-line', href: 'https://instagram.com', label: 'Instagram' },
                    { icon: 'ri-facebook-fill', href: 'https://facebook.com', label: 'Facebook' },
                    { icon: 'ri-linkedin-fill', href: 'https://linkedin.com', label: 'LinkedIn' },
                    { icon: 'ri-twitter-x-line', href: 'https://twitter.com', label: 'Twitter' },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:border-amber-500 hover:text-amber-900 transition-colors cursor-pointer"
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Get Started CTA */}
              <div className="border border-amber-200 rounded-2xl p-6 bg-amber-50">
                <p className="text-sm font-semibold text-gray-900 mb-2">Ready to get started?</p>
                <p className="text-xs text-gray-600 mb-4">
                  Fill out our detailed questionnaire so we can recommend the best path for your hosting goals.
                </p>
                <Link
                  to="/get-started"
                  className="inline-flex items-center px-5 py-2.5 bg-amber-900 text-white rounded-full text-xs font-medium hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Start Your Application
                  <i className="ri-arrow-right-line ml-1.5"></i>
                </Link>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 bg-gray-50 rounded-3xl">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-emerald-600"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h2>
                  <p className="text-gray-600 max-w-sm mx-auto mb-8 text-sm leading-relaxed">
                    Thank you for reaching out. We've received your message and will get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center px-6 py-2.5 border border-gray-300 text-gray-700 rounded-full text-sm font-medium hover:border-amber-500 hover:text-amber-900 transition-colors cursor-pointer"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm space-y-5"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Smith"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 234 567 8900"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Subject
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        'General Inquiry',
                        'Services',
                        'Coaching',
                        'Pricing',
                        'Partnership',
                        'Other',
                      ].map((subject) => (
                        <label
                          key={subject}
                          className="flex items-center p-3 border border-gray-200 rounded-xl cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-all text-sm has-[:checked]:border-amber-600 has-[:checked]:bg-amber-50"
                        >
                          <input
                            type="radio"
                            name="subject"
                            value={subject}
                            className="mr-2 accent-amber-900"
                          />
                          <span className="text-gray-700">{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-500 bg-gray-50 resize-none transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-amber-900 text-white rounded-full text-sm font-medium hover:bg-amber-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Send Message
                        <i className="ri-send-plane-line ml-2"></i>
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
